import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { salesApi, apiError } from '../salesApi';
import { useT } from '../salesI18n';
import { useSalesAuth } from '../SalesAuthContext';
import { SignaturePad, padPng } from './proposal/SignaturePad';

// "Minha assinatura": o vendedor desenha uma vez e ela entra automaticamente nos contratos.
export const SignatureDialog = ({ open, onOpenChange }) => {
  const { t } = useT();
  const { user, refresh } = useSalesAuth();
  const padRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !user?.has_signature) { setPreview(null); return undefined; }
    let url;
    salesApi.get('/auth/signature', { responseType: 'blob' }).then((r) => { url = URL.createObjectURL(r.data); setPreview(url); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [open, user?.has_signature]);

  const save = async () => {
    const png = padPng(padRef);
    if (!png) { toast.error(t.signatureRequired); return; }
    setBusy(true);
    try { await salesApi.put('/auth/signature', { signature_png_base64: png }); await refresh(); toast.success(t.signatureSaved); onOpenChange(false); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { await salesApi.delete('/auth/signature'); await refresh(); toast.success(t.signatureRemoved); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="signature-dialog">
        <DialogHeader><DialogTitle className="font-display">{t.mySignature}</DialogTitle><p className="text-xs text-slate-500">{t.mySignatureHint}</p></DialogHeader>
        {user?.has_signature ? (
          <div className="flex items-center justify-between gap-3 rounded-2xl border border-teal-200 bg-teal-50/60 p-3" data-testid="signature-current">
            <div className="flex items-center gap-3 min-w-0">{preview ? <img src={preview} alt="" className="h-14 object-contain bg-white rounded-lg px-2" /> : <span className="h-14 w-32 rounded-lg bg-white animate-pulse" />}<span className="text-xs font-semibold text-teal-800">{t.savedSignature}</span></div>
            <button type="button" onClick={remove} disabled={busy} className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700 hover:underline shrink-0" data-testid="signature-remove"><Trash2 className="h-3.5 w-3.5" /> {t.removeSignature}</button>
          </div>
        ) : <p className="text-xs text-slate-500" data-testid="signature-none">{t.noSavedSignature}</p>}
        <SignaturePad ref={padRef} label={t.signHere.replace(/ \(.*\)$/, '')} clearLabel={t.clearSignature} testId="signature-pad" />
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" className="h-11" onClick={() => onOpenChange(false)}>{t.cancel}</Button>
          <Button type="button" className="h-11 font-semibold" onClick={save} disabled={busy} data-testid="signature-save">{busy ? t.loading : t.saveSignature}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
